import { Trans, useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Pagination — count on the left, navigation on the right; current page in
// brand blue with aria-current; ends disabled (design/components/data-table.html
// §7.2). Compact page list with an ellipsis for long ranges.

export interface PaginationProps {
  /** 1-based current page. */
  page: number
  pageCount: number
  total: number
  pageSize: number
  onPage: (page: number) => void
  /** Noun for the count text (default "resultados"). */
  noun?: string
  className?: string
}

function pageItems(page: number, pageCount: number): (number | 'dots')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1)
  const items: (number | 'dots')[] = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(pageCount - 1, page + 1)
  if (start > 2) items.push('dots')
  for (let i = start; i <= end; i++) items.push(i)
  if (end < pageCount - 1) items.push('dots')
  items.push(pageCount)
  return items
}

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[13px]"
    >
      <path d={dir === 'left' ? 'm15 6-6 6 6 6' : 'm9 6 6 6-6 6'} />
    </svg>
  )
}

const btn =
  'flex h-7 min-w-7 items-center justify-center rounded-md border border-line bg-surface px-[7px] text-[12px] font-semibold text-text-2 transition-colors hover:bg-hover hover:text-text disabled:cursor-not-allowed disabled:bg-surface disabled:text-text-3 disabled:hover:bg-surface'

/** "Mostrando X–Y de N" count plus page navigation: 1-based pages, current in
 *  brand blue with aria-current, ends disabled, ellipsis past 7 pages. The
 *  count derives from total + pageSize; `noun` swaps "resultados". */
export function Pagination({
  page,
  pageCount,
  total,
  pageSize,
  onPage,
  noun: nounProp,
  className,
}: PaginationProps) {
  const { t } = useTranslation('components')
  const noun = nounProp ?? t('ui.pagination.defaultNoun')
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(total, page * pageSize)
  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <span className="text-[12px] text-text-2">
        <Trans
          i18nKey="ui.pagination.showing"
          ns="components"
          values={{ from, to, total, noun }}
          components={{ 1: <b className="font-semibold text-text" /> }}
        />
      </span>
      <nav aria-label={t('ui.pagination.label')} className="ml-auto flex items-center gap-1">
        <button
          type="button"
          className={btn}
          aria-label={t('ui.pagination.previous')}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <Chevron dir="left" />
        </button>
        {pageItems(page, pageCount).map((it, i) =>
          it === 'dots' ? (
            <span key={`d${i}`} className="px-1 text-text-3" aria-hidden>
              …
            </span>
          ) : (
            <button
              key={it}
              type="button"
              aria-label={t('ui.pagination.page', { n: it })}
              aria-current={it === page ? 'page' : undefined}
              onClick={() => onPage(it)}
              className={cn(
                btn,
                it === page &&
                  'border-brand-blue bg-brand-blue text-white hover:bg-brand-blue hover:text-white',
              )}
            >
              {it}
            </button>
          ),
        )}
        <button
          type="button"
          className={btn}
          aria-label={t('ui.pagination.next')}
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
        >
          <Chevron dir="right" />
        </button>
      </nav>
    </div>
  )
}
